import {useEffect, useMemo, useRef, useState} from 'react';
import type {KeyboardEvent} from 'react';
import {
  useRichTextSelection,
  useRichTextEditor,
  RichTextEditor as OsuiEditor,
  useRichTextPreset,
  RichTextPreset,
  LinkType,
  HeadingPlugin,
  headingHotKeyHandler,
  ParagraphType,
  EditableLink,
  unwrapLinks,
  getLinkAndText,
  upsertLink,
  serializeToHTML,
  deserializeHTML,
} from '@shopify/online-store-ui';
import type {
  RichTextEditorSelection as Selection,
  RichTextEditorValue as Value,
} from '@shopify/online-store-ui';
import isEqual from 'lodash/isEqual';
import {StoreOnlineIcon, LinkIcon} from '@shopify/polaris-icons';
import {useI18n} from '@shopify/react-i18n';
import {isAbsolute} from '@web-utilities/url';
import {useDebouncedCallback} from 'use-debounce';
import Bugsnag from '@bugsnag/js';

import {
  deserializeFromShopAST,
  safeParse,
  serializeToShopAST,
} from './utilities';
import {EMPTY_SELECTION, EMPTY_SHOP_AST} from './constants';
import {useUndoRedo} from './hooks';
import type {LinkEditModalValue} from './components';
import {EditorToolbar, LinkEditModal} from './components';
import styles from './RichTextEditor.scss';
import {ShopASTNode} from './types';

interface RichTextState {
  value: Value;
  selection: Selection;
}

interface Props {
  value: string;
  onChange(newValue: string): void;
  onFocus(): void;
  onBlur(): void;
  readOnly?: boolean;
  mode?: 'json' | 'html';
  preferredPreset?: 'metafield' | 'themeEditor' | 'inlineHtml';
}

export function RichTextEditor({
  value: initialValue,
  onChange,
  onFocus,
  onBlur,
  readOnly,
  mode = 'json',
  preferredPreset = 'themeEditor',
}: Props) {
  const [i18n] = useI18n();
  const [modalVisible, setModalVisibility] = useState(false);
  const changePending = useRef(true);
  const [initialValueSet, setInitialValueSet] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState<Value>([]);
  const preset = useMemo(() => {
    switch (preferredPreset) {
      case 'metafield':
        return RichTextPreset.MetafieldRichText;
      case 'inlineHtml':
        return RichTextPreset.InlineRichText;
      case 'themeEditor':
      default:
        return RichTextPreset.ThemeEditorRichText;
    }
  }, [preferredPreset]);

  const {plugins} = useRichTextPreset(
    preset,
    useMemo(() => {
      const additionalPlugins = [];

      const updateSelection = (selection: Selection) => {
        if (selection) {
          restoreSelection(selection);
        } else {
          restoreSelection(EMPTY_SELECTION);
        }
      };

      if (preset !== RichTextPreset.InlineRichText) {
        additionalPlugins.push(
          HeadingPlugin({
            onHotkey: (keyboardEvent: KeyboardEvent) => {
              headingHotKeyHandler(editor, keyboardEvent, ParagraphType);
            },
          }),
        );
      }

      return {
        [LinkType]: {
          onHotkey() {
            setModalVisibility(true);
            blur();
          },
          renderPopover: modalVisible
            ? undefined
            : (editor, element) => {
                const link = {
                  url: element.url,
                  title: element.title,
                  icon: StoreOnlineIcon,
                };
                if (element.url) {
                  link.icon = isAbsolute(element.url)
                    ? LinkIcon
                    : StoreOnlineIcon;
                }

                return {
                  accessibilityAnnouncement: i18n.translate(
                    'linkAccessibilityAnnouncement',
                  ),

                  markup: (
                    <EditableLink
                      url={link.url}
                      label={link.title}
                      icon={link.icon}
                      onEdit={() => {
                        setModalVisibility(true);
                        blur();
                      }}
                      onRemove={() => unwrapLinks(editor)}
                    />
                  ),
                };
              },
        },
        sideEffects: [],
        additionalPlugins,
        hotkeys: [
          {
            // undo
            hotkey: 'Ctrl+Z',
            handler: () => {
              if (changePending.current) {
                return;
              }
              blur();
              const {selection, value} = pluginRef.current.goBack();
              updateMetafieldAST(value);
              updateSelection(selection);
            },
          },
          {
            // redo
            hotkey: 'Ctrl+Shift+Z',
            handler: () => {
              if (changePending.current) {
                return;
              }
              blur();
              const {selection, value} = pluginRef.current.goForward();
              updateMetafieldAST(value);
              updateSelection(selection);
            },
          },
          {
            // prevent link hotkey from opening global search
            hotkey: 'Ctrl+K',
            handler: (_, event) => {
              event.stopPropagation();
            },
          },
        ],
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  const {
    state: editorValue,
    setState: setEditorValue,
    goBack,
    goForward,
    reset: resetEditorState,
    setInitialState,
  } = useUndoRedo<RichTextState>({
    value: debouncedValue,
    selection: null,
  });

  const pluginRef = useUpdatingRef({
    goBack,
    goForward,
    onChange,
    setEditorValue,
    resetEditorState,
    setInitialState,
  });

  const editor = useRichTextEditor({
    plugins,
  });

  const {selection, setSelection, blur, restoreSelection} =
    useRichTextSelection(editor);

  useEffect(() => {
    if (initialValueSet) return;

    const toJSONValue = (
      value: string,
      defaultValue: ShopASTNode,
    ): ShopASTNode => (value ? safeParse(value, defaultValue) : defaultValue);

    let initEditorValue;

    if (mode === 'json') {
      initEditorValue = deserializeFromShopAST(
        toJSONValue(initialValue, EMPTY_SHOP_AST),
      );
    } else {
      try {
        initEditorValue = deserializeHTML(initialValue, {editor, plugins});
      } catch (error: any) {
        Bugsnag.notify(
          `[RichTextEditor] Error deserializing HTML. Error: ${
            error instanceof Error ? error.message : error
          }. Original value: ${initialValue}`,
        );
      } finally {
        initEditorValue = deserializeHTML(initialValue, {
          editor,
          plugins,
          normalize: false,
        });
      }
    }

    changePending.current = false;
    setDebouncedValue(initEditorValue);
    setInitialValueSet(true);
    pluginRef.current.setEditorValue({value: initEditorValue, selection});
    pluginRef.current.setInitialState({
      value: initEditorValue,
      selection: null,
    });
  }, [
    editor,
    initialValue,
    initialValueSet,
    mode,
    pluginRef,
    plugins,
    selection,
  ]);

  const handleChange = (value: Value) => {
    changePending.current = false;
    pluginRef.current.setEditorValue({value, selection});
    if (mode === 'json') {
      updateMetafieldAST(value);
    } else {
      updateHTML();
    }
  };
  const debouncedOnChange = useDebouncedCallback(handleChange, 500);

  const updateHTML = () => {
    pluginRef.current.onChange(serializeToHTML(editor.children, {plugins}));
  };

  const handleToolbarAction = (type: string) => {
    setSelection(selection);
    if (type === LinkType) {
      setModalVisibility(true);
      blur();
    }
  };

  const modalMarkup = modalVisible ? getModalMarkup() : null;

  function getModalMarkup() {
    const {link, text} = getLinkAndText(editor);
    const value = {
      url: link?.url,
      title: link?.title,
      targetBlank: link?.target,
      text: text ?? '',
      hasLink: Boolean(link),
    };

    return (
      <LinkEditModal
        open={modalVisible}
        link={value}
        onClose={() => setModalVisibility(false)}
        handleUpsert={handleLinkModalUpsert}
        handleRemove={handleLinkModalRemove}
      />
    );
  }

  function handleLinkModalUpsert(modalData: LinkEditModalValue) {
    const {url, title, targetBlank, userInputText, initialText} = modalData;
    const text = userInputText === initialText ? undefined : userInputText;

    if (url && selection) {
      upsertLink(editor, {
        at: editor.selection,
        text: text ?? undefined,
        data: {
          url,
          title,
          target: targetBlank ? '_blank' : undefined,
        },
      });
    }

    setModalVisibility(false);
  }

  function handleLinkModalRemove() {
    unwrapLinks(editor, selection);
    setModalVisibility(false);
  }

  const updateMetafieldAST = (value: Value) => {
    const shopAST = serializeToShopAST(value);
    const emptyShopAST =
      shopAST?.children.length === 1 &&
      shopAST?.children[0]?.children?.length === 1 &&
      shopAST?.children[0]?.children[0]?.value === '';
    if (!shopAST || emptyShopAST) {
      return pluginRef.current.onChange('');
    }

    const stringifiedShopAST = JSON.stringify(shopAST);
    pluginRef.current.onChange(stringifiedShopAST);
  };

  const toolbarMarkup = (
    <EditorToolbar
      plugins={plugins}
      onAction={handleToolbarAction}
      disabled={readOnly}
    />
  );

  return (
    <>
      <div className={styles.Root}>
        <OsuiEditor
          readOnly={readOnly}
          editor={editor}
          plugins={plugins}
          onChange={(value) => {
            if (!isEqual(value, editorValue.value)) {
              changePending.current = true;
              debouncedOnChange(value);
            }
          }}
          onSelectionChange={setSelection}
          toolbar={toolbarMarkup}
          selection={selection}
          value={editorValue.value}
          onFocus={onFocus}
          onBlur={onBlur}
          unframed
          hideFocusRing
          fullWidth
        />
        {modalMarkup}
      </div>
    </>
  );
}

function useUpdatingRef<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
